import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import DocViewer, {
	DocViewerRenderers,
	IConfig,
	IDocument,
	IRenderSettings,
} from "@egorcloud/react-doc-viewer";
import Loader from "rsuite/Loader";
import { noop } from "lodash";

import File from "../../services/File";
import useObjectEditor from "../../hooks/useObjectEditor";
import Modal from "../Modal";
import DeleteModal from "../DeleteModal";

import Root from "./components/Root";
import Header from "./components/Header";
import NoRenderer from "./components/NoRenderer";
import Controls from "./components/Controls";

function inferMimeFromFileName(fileName: string): string | undefined {
	const ext = fileName.split(".").pop()?.toLowerCase();
	switch (ext) {
		case "png":
			return "image/png";
		case "jpg":
		case "jpeg":
			return "image/jpeg";
		case "gif":
			return "image/gif";
		case "webp":
			return "image/webp";
		case "bmp":
			return "image/bmp";
		case "pdf":
			return "application/pdf";
		default:
			return undefined;
	}
}

function viewerMimeHint(headerType: string, blob: Blob, fileName: string): string {
	const header = headerType.split(";")[0].trim();
	if (header && header !== "application/octet-stream") return header;

	const blobType = (blob.type || "").split(";")[0].trim();
	if (blobType && blobType !== "application/octet-stream") return blobType;

	return inferMimeFromFileName(fileName) ?? "application/octet-stream";
}

const PreloadedImageViewport = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
	width: 100%;
	overflow-y: auto;
`;

const PreloadedViewerShell = styled.div`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	width: 100%;
	height: 100vh;
	background: rgba(0, 0, 0, 0.7);
`;

const PreloadedImage = styled.img`
	max-width: 95%;
	max-height: 95%;
`;

const FileViewerModal: React.FC<FileViewerModal.Props> = ({
	disabled,
	value,
	preloadedImageUrl,

	onDelete,
	onClose,
}) => {
	const { t } = useTranslation();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const isPreloadedImage = Boolean(preloadedImageUrl);

	const [document, setDocument] =
		useState<FileViewerModal.Document | undefined>(() =>
			isPreloadedImage
				? {
						uri: preloadedImageUrl!,
						fileType:
							inferMimeFromFileName(value.name) ??
							(value.type.split(";")[0] || "image/png"),
						fileName: value.name,
						base: value,
				  }
				: undefined,
		);

	const [controller, setController] = React.useState<any>(null);
	const [settings, setSettings] = React.useState<IRenderSettings>({
		loaded: isPreloadedImage,
		zoomLevel: 1,
		paginated: false,
		pagesCount: 1,
		currentPage: 0,
		rotationAngle: 0,
		fitType: "page",
	});

	useEffect(() => {
		if (!settings || !controller) return;

		controller.update(settings);
	}, [controller, settings]);

	const settingsEditor = useObjectEditor(settings ?? {}, setSettings);

	const documents = useMemo(() => (document ? [document] : []), [document]);

	const headerOnDelete = useCallback(() => setShowDeleteModal(true), []);

	const config = useMemo<IConfig>(
		() => ({
			header: {
				disableFileName: false,
				overrideComponent: (state) => (
					<Header
						{...state}
						disabled={disabled}
						onDelete={headerOnDelete}
						onClose={onClose ?? noop}
					/>
				),
			},
			loadingRenderer: {
				overrideComponent: Loader,
			},
			noRenderer: {
				overrideComponent: NoRenderer,
			},
		}),
		[disabled, headerOnDelete, onClose],
	);

	const headerState = useMemo(
		() =>
			document
				? {
						currentFileNo: 0,
						documents: [document],
						currentDocument: document,
						documentLoading: false,
						config,
						pluginRenderers: DocViewerRenderers,
				  }
				: undefined,
		[document, config],
	);

	const docViewerOnLoaded = useCallback((data: any) => {
		setSettings(data.state);
		setController(data.controller);
	}, []);

	const deleteModalOnCancel = useCallback(
		() => setShowDeleteModal(false),
		[],
	);

	const deleteModalOnConfirm = useCallback(() => {
		onDelete?.();
		setShowDeleteModal(false);
	}, [onDelete]);

	const controlsOnZoomIn = useCallback(() => {
		settingsEditor.set(
			"zoomLevel",
			Math.min(settingsEditor.get("zoomLevel") + 0.1, 3.5),
		);
	}, [settingsEditor]);

	const controlsOnZoomOut = useCallback(() => {
		settingsEditor.set(
			"zoomLevel",
			Math.max(settingsEditor.get("zoomLevel") - 0.1, 0.2),
		);
	}, [settingsEditor]);

	const controlsOnCounterclockwiseRotate = useCallback(() => {
		settingsEditor.set(
			"rotationAngle",
			(settingsEditor.get("rotationAngle") - 90) % 360,
		);
	}, [settingsEditor]);

	const controlsOnResetRotation = useCallback(() => {
		settingsEditor.set("rotationAngle", 0);
	}, [settingsEditor]);

	const controlsOnClockwiseRotate = useCallback(() => {
		settingsEditor.set(
			"rotationAngle",
			(settingsEditor.get("rotationAngle") + 90) % 360,
		);
	}, [settingsEditor]);

	const controlsOnFill = useCallback(() => {
		settingsEditor.set("zoomLevel", 1);
	}, [settingsEditor]);

	const controlsOnFit = useCallback(() => {
		settingsEditor.set("zoomLevel", 0.46);
	}, [settingsEditor]);

	useEffect(() => {
		if (preloadedImageUrl) return undefined;

		let objectUrl: string | undefined;
		let cancelled = false;

		setDocument(undefined);

		(async () => {
			const blob = await value.load();
			if (cancelled || !blob) return;

			objectUrl = URL.createObjectURL(blob);
			if (cancelled) {
				URL.revokeObjectURL(objectUrl);
				return;
			}

			setDocument({
				uri: objectUrl,
				fileType: viewerMimeHint(value.type, blob, value.name),
				fileName: value.name,
				base: value,
			});
		})();

		return () => {
			cancelled = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [value.id, value.name, value.type, preloadedImageUrl]);

	let viewerContent: React.ReactNode = (
		<div
			style={{
				display: "flex",
				flex: 1,
				alignItems: "center",
				justifyContent: "center",
				minHeight: "calc(100vh - 64px)",
			}}
		>
			<Loader />
		</div>
	);

	if (isPreloadedImage && preloadedImageUrl && headerState) {
		viewerContent = (
			<PreloadedViewerShell id="react-doc-viewer">
				<Header
					{...headerState}
					disabled={disabled}
					onDelete={headerOnDelete}
					onClose={onClose ?? noop}
				/>
				<PreloadedImageViewport id="proxy-renderer">
					<PreloadedImage
						id="image-img"
						src={preloadedImageUrl}
						alt={value.name}
						style={{
							transform: `scale(${settings.zoomLevel}) rotate(${settings.rotationAngle}deg)`,
						}}
					/>
				</PreloadedImageViewport>
			</PreloadedViewerShell>
		);
	} else if (document) {
		viewerContent = (
			<DocViewer
				key={value.id}
				pluginRenderers={DocViewerRenderers}
				renderSettings={settings}
				documents={documents}
				config={config}
				onChange={setSettings}
				onLoaded={docViewerOnLoaded}
			/>
		);
	}

	return (
		<>
			<Modal dimmed>
				<Root>
					{viewerContent}
					<Controls
						onZoomIn={controlsOnZoomIn}
						onZoomOut={controlsOnZoomOut}
						onCounterclockwiseRotate={
							controlsOnCounterclockwiseRotate
						}
						onResetRotation={controlsOnResetRotation}
						onClockwiseRotate={controlsOnClockwiseRotate}
						onFill={controlsOnFill}
						onFit={controlsOnFit}
					/>
				</Root>
			</Modal>
			{showDeleteModal ? (
				<DeleteModal
					disabled={disabled}
					label={t("fileViewerModal.str200") ?? ""}
					onCancel={deleteModalOnCancel}
					onConfirm={deleteModalOnConfirm}
				/>
			) : null}
		</>
	);
};

declare namespace FileViewerModal {
	type Value = File.Model;

	interface Document extends IDocument {
		base: Value;
	}

	interface Props {
		disabled: boolean;
		value: Value;
		/** Blob/object URL already loaded (receipt preview). Skips DocViewer. */
		preloadedImageUrl?: string;

		onDelete?: () => void;
		onClose?: () => void;
	}
}

export default FileViewerModal;
